import { lazy, Suspense, useState } from 'react'
import { LoopApiError } from '@innerloopco/loopcms-core'
import { Button, Icon, Input, Seg } from '../../components/index.js'
import { relativeTime } from '../../lib/relativeTime.js'
import { SiteMapList } from './SiteMapList.js'
import { useRefreshSiteMap, useSiteMap } from './useSiteMap.js'
import '../../styles/site-map.css'

// elkjs is heavy and only the Map needs it, so the Map loads on demand.
const SiteMapMap = lazy(() => import('./SiteMapMap.js'))

type View = 'list' | 'map'
const VIEW_KEY = 'loop.siteMap.view'

const readView = (): View => {
  try {
    return window.localStorage.getItem(VIEW_KEY) === 'map' ? 'map' : 'list'
  } catch {
    return 'list'
  }
}
const saveView = (view: View) => {
  try {
    window.localStorage.setItem(VIEW_KEY, view)
  } catch {
    // Private windows can refuse storage; the view just isn't remembered.
  }
}

const hostOf = (url: string) => {
  try {
    return new URL(url).host
  } catch {
    return url
  }
}

/**
 * Site map: every page of the site and the links between them, as an outline
 * (List, the default) or an automatically laid-out tree (Map). Links come from
 * reading the live site in the background, plus links in content.
 */
export function SiteMapScreen() {
  const { data: map, error, isLoading } = useSiteMap()
  const refresh = useRefreshSiteMap()
  const [view, setView] = useState<View>(readView)
  const [query, setQuery] = useState('')

  const crawl = map?.crawl ?? null
  const status = !crawl
    ? null
    : crawl.running
      ? 'Reading the site…'
      : crawl.error
        ? `${crawl.error} — links from its menus and listings aren't shown.`
        : crawl.finishedAt
          ? `Links read from ${hostOf(crawl.siteUrl)}, ${relativeTime(crawl.finishedAt)}`
          : null

  return (
    <div className="screen stack" style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
      <header className="lc-page-header">
        <h1 style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em', margin: 0, flex: '1 1 auto' }}>Site map</h1>
        {map ? <span className="text-muted" style={{ fontSize: 12.5 }}>{map.pages.length} pages · {map.links.length} links</span> : null}
      </header>

      <div className="lc-page-body stack" style={{ gap: 'var(--space-4)' }}>
        <div className="row lc-sm-toolbar">
          <Input
            type="search"
            aria-label="Find a page"
            placeholder="Find a page"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            style={{ width: 260, flex: 'none' }}
          />
          <Seg
            name="site-map-view"
            value={view}
            options={[{ value: 'list', label: 'List' }, { value: 'map', label: 'Map' }]}
            onChange={(next) => {
              setView(next)
              saveView(next)
            }}
          />
          <span className={`lc-sm-status${crawl?.error ? ' is-error' : ''}`}>{status}</span>
          <Button variant="secondary" onClick={() => refresh.mutate()} disabled={refresh.isPending || crawl?.running === true}>
            <Icon name="ArrowsClockwise" size={14} /> Refresh
          </Button>
        </div>

        {error ? (
          <p role="alert" className="row" style={{ gap: 6, color: 'var(--color-danger)', fontSize: 13 }}>
            <Icon name="Warning" size={14} />
            {error instanceof LoopApiError ? error.message : 'Could not load the site map.'}
          </p>
        ) : isLoading || !map ? (
          <p className="text-muted">Loading…</p>
        ) : view === 'list' ? (
          <SiteMapList map={map} query={query} />
        ) : (
          <Suspense fallback={<p className="text-muted">Loading the map…</p>}>
            <SiteMapMap map={map} query={query} />
          </Suspense>
        )}
      </div>
    </div>
  )
}
